// src/resources/dashboards/Painel360/PenetrationSection.tsx
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { stringify } from 'query-string';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtNum, fmtPct, openInNewTab } from './shared';
import { PenetrationData, SectionState } from './types';
import HeatmapAiInsight from './HeatmapAiInsight';

const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']; // dayOfWeek 0 = domingo (EXTRACT(DOW))

// Texto isolado numa constante pra ser fácil de trocar — ninguém gostou de
// "ver mais"/"ver detalhes" no brainstorm, mas todo mundo concordou que o
// texto ia mudar de novo, então não fica espalhado no JSX.
const DETAILS_BUTTON_LABEL = 'Ver detalhes por posto';

const Gauge = ({ pct, deltaPp }: { pct: number; deltaPp: number | null }) => {
  const circumference = 2 * Math.PI * 60;
  return (
    <Box sx={{ position: 'relative', width: 150, height: 150 }}>
      <svg width={150} height={150} viewBox="0 0 150 150">
        <circle cx={75} cy={75} r={60} fill="none" stroke="#f1f5f9" strokeWidth={16} />
        <motion.circle
          cx={75} cy={75} r={60} fill="none" stroke={COLORS.blue} strokeWidth={16} strokeLinecap="round"
          strokeDasharray={circumference} transform="rotate(-90 75 75)"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference * (1 - pct) }}
          transition={{ duration: 1.1, ease: [0.25, 0.1, 0.25, 1] }}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: 28, fontWeight: 800, color: COLORS.ink }}>{fmtPct(pct)}</Typography>
        {deltaPp !== null && (
          <Typography sx={{ fontSize: 10, color: deltaPp >= 0 ? COLORS.success : COLORS.danger }}>
            {deltaPp >= 0 ? '▲' : '▼'} {Math.abs(deltaPp).toLocaleString('pt-BR')}%
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface PenetrationSectionProps {
  state: SectionState<PenetrationData>;
  // Mesmo período/filtro que gerou os dados desta seção — usados só pra montar
  // o link de drill-down com o filtro já aplicado (react-admin lê `filter` da
  // query string). `from`/`to` chegam CRUS ('YYYY-MM-DD', o estado do cabeçalho);
  // openDetails converte pra borda certa antes de navegar.
  from: string;
  to: string;
  stationIds: string[];
  chainId: string;
}

const PenetrationSection = ({ state, from, to, stationIds, chainId }: PenetrationSectionProps) => {

  if (state.loading) return <SkeletonCard height={320} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={320} />;
  if (!state.data) return null;
  const { available, overall, byStation, hourHeatmap } = state.data;

  const heatmapMax = Math.max(1, ...hourHeatmap.map(h => h.fillins));
  const heatmapMap = new Map(hourHeatmap.map(h => [`${h.dayOfWeek}-${h.hour}`, h.fillins]));

  const openDetails = () => {
    // O relatório usa a mesma convenção do BetweenDatesInput: `from` no começo do dia e `to` no
    // FIM do dia, em ISO. Mandar o `to` cru ('YYYY-MM-DD') deixaria o dia inteiro de fora (o
    // backend trata `to` como exclusivo) e os números do relatório não bateriam com os desta
    // seção, que consulta com to+1 dia.
    const filter = {
      from: dayjs(from).startOf('day').toISOString(),
      to: dayjs(to).endOf('day').toISOString(),
      ...(stationIds.length ? { stationIds } : {}),
      ...(chainId ? { chainId } : {}),
    };
    openInNewTab(`/report-app-penetration?${stringify({ filter: JSON.stringify(filter) })}`);
  };

  return (
    <Box>
      <SectionHeader
        icon={<SensorsIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.blue}, #06b6d4)`}
        title="Penetração do app na pista"
        subtitle="Tudo que passou pela bomba (com e sem app) — quanto você está identificando?"
      />
      {!available ? (
        <Box sx={cardSx}>
          <EmptyHint text="Dados da pista indisponíveis para esta rede (sem concentrador integrado)." />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.75 }}>
          <Box sx={cardSx}>
            {/* Botão dentro do card, na mesma linha do título: é daqui que os números saem,
                e no cabeçalho da seção ele ficava solto, fora do quadro. */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Captura da rede</Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>% da pista identificado pelo app — em litros e em nº de abastecimentos</Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={openDetails}
                endIcon={<ArrowForwardIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {DETAILS_BUTTON_LABEL}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
              {overall.appPct !== null && (
                <Box sx={{ textAlign: 'center' }}>
                  <Gauge pct={overall.appPct} deltaPp={overall.deltaPp} />
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: COLORS.faint, mt: -0.5 }}>Volume (litros)</Typography>
                </Box>
              )}
              {/* Penetração por nº de abastecimentos, ao lado do volume */}
              {overall.appFillinsPct !== null && (
                <Box sx={{ textAlign: 'center', minWidth: 90 }}>
                  <Typography sx={{ fontSize: 28, fontWeight: 800, color: COLORS.ink }}>{fmtPct(overall.appFillinsPct)}</Typography>
                  {overall.deltaPpFillins !== null && (
                    <Typography sx={{ fontSize: 10, color: overall.deltaPpFillins >= 0 ? COLORS.success : COLORS.danger }}>
                      {overall.deltaPpFillins >= 0 ? '▲' : '▼'} {Math.abs(overall.deltaPpFillins).toLocaleString('pt-BR')}%
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: COLORS.faint }}>Abastecimentos</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 220 }}>
                {byStation.slice(0, 6).map(s => (
                  <Box key={s.placeId} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1, fontSize: 12.5 }}>
                    <Typography sx={{ width: 130, fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</Typography>
                    <Box sx={{ flex: 1, height: 14, bgcolor: '#f1f5f9', borderRadius: 1, overflow: 'hidden' }}>
                      <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(100, (s.appPct ?? 0) * 200)}%` }} transition={{ duration: 0.9 }}
                        style={{ height: '100%', borderRadius: 4, background: (s.appPct ?? 0) >= 0.15 ? `linear-gradient(90deg, ${COLORS.blue}, #06b6d4)` : `linear-gradient(90deg, ${COLORS.warning}, #fbbf24)` }} />
                    </Box>
                    <Typography sx={{ width: 90, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>
                      <b>{fmtPct(s.appPct)}</b> <Typography component="span" sx={{ color: COLORS.faint, fontSize: 11 }}>· {fmtPct(s.appFillinsPct)} ab.</Typography>
                    </Typography>
                  </Box>
                ))}
                <Typography sx={{ fontSize: 11.5, color: COLORS.muted, mt: 1 }}>
                  Volume: <b>{fmtNum(overall.appVolume)} L</b> de <b>{fmtNum(overall.totalVolume)} L</b> · Abastecimentos: <b>{fmtNum(overall.appFillins)}</b> de <b>{fmtNum(overall.totalFillins)}</b>
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Movimento por hora × dia</Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>Toda a pista — planeje equipe e promoções nos horários certos</Typography>
            {!hourHeatmap.length ? <EmptyHint text="Sem movimento registrado no período." /> : (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '34px repeat(24, 1fr)', gap: '3px' }}>
                  <Box />
                  {Array.from({ length: 24 }, (_, h) => (
                    <Box key={h} sx={{ fontSize: 9, color: COLORS.faint, textAlign: 'center' }}>{h % 3 === 0 ? h : ''}</Box>
                  ))}
                  {DAYS.map((d, di) => (
                    <React.Fragment key={d}>
                      <Box sx={{ fontSize: 10, fontWeight: 700, color: COLORS.muted, display: 'flex', alignItems: 'center' }}>{d}</Box>
                      {Array.from({ length: 24 }, (_, h) => {
                        const v = heatmapMap.get(`${di}-${h}`) || 0;
                        return (
                          <Box key={h} title={`${d} ${h}h · ${fmtNum(v)} abastecimentos`}
                            sx={{ aspectRatio: '1', borderRadius: 0.5, bgcolor: `rgba(59,130,246,${(0.06 + (v / heatmapMax) * 0.84).toFixed(2)})` }} />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
                {/* "Leitura da IA" — feature 157. Só quando o heatmap tem dado (mesma
                    condição do grid acima); o componente mesmo se esconde em unavailable/erro. */}
                <HeatmapAiInsight stationIds={stationIds} />
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PenetrationSection;
